/**
 * pi 引擎的选线规则（N-121 批次 B）——一个模型在 pi 下走哪种接口格式，**只**由网关清单决定。
 *
 * 产品原则（2026-10-08 产品经理拍板）：pi 走哪种接口只在该模型「上游原生」（网关 `translated === false`）
 * 的协议行里选，绝不选网关翻译出来的行，也不按模型名字猜。能不能进 pi = 至少有一条原生行、且那种接口
 * DOSIA 的 pi 有腿；走哪一条由下面的家族偏好表决定。
 *
 * 本文件是叶子模块（只 import 同为叶子的 `catalog-row-supply`——零账号行判据的单源）：清单解析（bytespike-bundle-wire）、选线消费方
 * （model-registry）、双腿协议清单生成脚本（scripts/dual-leg-protocol-list.mjs）三处读的是同一张表、
 * 同一个函数，没有第二份。
 */
import { isNoCredentialRow } from "./catalog-row-supply.js";
/**
 * 表外家族（或清单没给 `family_slug`）的偏好顺序：Chat → Responses → Anthropic → Gemini。
 * 同时也是「DOSIA pi 有腿的接口」全集——不在这里的协议（如 `antigravity`）永远不进 pi。
 */
export const PI_WIRE_DEFAULT_PREFERENCE = [
    "openai_chat_completions",
    "openai_responses",
    "anthropic_messages",
    "gemini",
];
/**
 * 按网关 `family_slug` 的 pi 原生接口偏好（仓内唯一一张表；理由是数据，生成脚本原样写进清单文档）。
 * 只决定「走哪个原生接口」，不决定「能不能进 pi」：家族首选接口不是原生时，按默认顺序在其余原生行里接着挑。
 */
export const PI_WIRE_PREFERENCE_BY_FAMILY = {
    openai: {
        prefer: ["openai_responses"],
        reason: "OpenAI 官方：GPT-6 的 Chat 面只在 reasoning_effort=none 时支持函数调用；网关 Chat 翻译腿丢 parallel_tool_calls",
    },
    grok: {
        prefer: ["openai_responses"],
        reason: "xAI 只开 Responses；网关的 Chat / Anthropic 面都是翻译出来的",
    },
    anthropic: {
        prefer: ["anthropic_messages"],
        reason: "Claude 唯一的原生面",
    },
    google: {
        prefer: ["gemini"],
        reason: "Gemini 唯一的原生面；网关翻译成 Chat 会丢 PDF、把思考折成正文、换掉函数签名",
    },
    deepseek: {
        prefer: ["openai_chat_completions"],
        reason: "DeepSeek 官方主面，思考字段回传、档位表达最完整；其 Responses 面把 developer 当 user、忽略 parallel_tool_calls",
    },
    glm: {
        prefer: ["openai_chat_completions"],
        reason: "智谱官方主面；thinking 开关与思考回传在 Chat 面",
    },
    kimi: {
        prefer: ["openai_chat_completions"],
        reason: "Kimi 官方主面；思考档位（reasoning_effort）与 reasoning_content 回传在 Chat 面",
    },
    mimo: {
        prefer: ["openai_chat_completions"],
        reason: "小米 MiMo 官方主面",
    },
    qwen: {
        prefer: ["openai_chat_completions"],
        reason: "千问（百炼）官方主面是 OpenAI 兼容 Chat；enable_thinking 与思考回传在这一面",
    },
};
/**
 * 暂不进 pi 的家族（B-185，维护者 2026-10-15 拍板「修好前 `[pi]` 变体不出现在选择器」）：键 = 网关 `family_slug`，
 * 值 = 理由（数据，随表进「双腿协议模型清单」文档的理由列）。命中的家族即使有上游原生行也**不是 pi 候选**——
 * {@link piNativeWireRow} 对它返回 undefined，于是 pi 菜单、凭证查找、route 投影、Pi 候选池（超管页与下发并入）、
 * 云端 agent-host 同时看不见它：它们都只经这一处选线。已选着该家族 `[pi]` 变体的员工，发送照现有「pi 链路丢失」
 * 护栏落到 Claude 引擎同一模型并在对话里提示（agent-bridge `piChainLost`，REQ-265）。
 * 挡的是家族，不是接口：别家走 Anthropic 原生腿不受影响。解除 = 删掉这一项，前提是网关侧修好并用 pi 腿实测通过。
 */
export const PI_WIRE_HELD_FAMILIES = {
    anthropic: "暂不进 pi：网关 Claude 上游拒收 pi 的 Anthropic 腿（「当前地区暂不支持访问模型」），同账号同分组同模型走 Claude 引擎正常，"
        + "差别只在发请求的客户端（B-185）；网关侧修好并用 pi 腿实测通过前不开放",
};
/** 该家族是否暂不进 pi（{@link PI_WIRE_HELD_FAMILIES}；只认自有键，原型链上的名字不算）。 */
export function isPiHeldFamily(familySlug) {
    const slug = familySlug?.trim();
    return !!slug && Object.hasOwn(PI_WIRE_HELD_FAMILIES, slug);
}
/** 该协议是不是 DOSIA pi 有腿的四种接口之一（类型守卫，入参是不可信的清单字符串）。 */
export function isPiWireProtocol(protocol) {
    return typeof protocol === "string" && PI_WIRE_DEFAULT_PREFERENCE.includes(protocol);
}
/**
 * 某家族的完整偏好顺序 = 家族首选（表里有）+ 默认顺序里的其余接口。家族缺席 / 不在表里 → 默认顺序。
 */
export function piWirePreferenceFor(familySlug) {
    const slug = familySlug?.trim();
    const own = slug && Object.hasOwn(PI_WIRE_PREFERENCE_BY_FAMILY, slug) ? PI_WIRE_PREFERENCE_BY_FAMILY[slug].prefer : undefined;
    if (!own)
        return PI_WIRE_DEFAULT_PREFERENCE;
    return [...own, ...PI_WIRE_DEFAULT_PREFERENCE.filter((wire) => !own.includes(wire))];
}
/** 一行能不能当 pi 的候选：原生（translated 明确 false）、没被标不支持、且 pi 有这条腿。 */
function isPiNativeCandidate(row) {
    return row.translated === false && row.supported !== false && isPiWireProtocol(row.protocol);
}
/**
 * **pi 选线的唯一规则**：给定**同一个 base 模型**的全部清单行，返回 pi 该走的那一行；没有可走的原生行 → undefined。
 *
 *  0. 家族在 {@link PI_WIRE_HELD_FAMILIES} 里（暂不进 pi）→ undefined，不看行。
 *  1. 候选 = `translated === false` 且 `supported !== false` 且协议是 pi 四条腿之一的行。`translated` 缺席的行
 *     不是候选（判不了不放行，与 N-119 Claude 引擎同一原则），解析入口会打 {@link PI_LEG_MISSING_TRANSLATED_LOG}。
 *  2. 按该模型 `family_slug` 的偏好顺序（{@link piWirePreferenceFor}）取第一个有候选的接口。
 *  3. 同一接口有多行（多个分组各出一行）时取输入顺序里的第一行——调用方的行序就是它的组优先序。
 *  4. 有账号的行优先（N-118）：零账号行（{@link isNoCredentialRow}，网关照常下发但该分组已没有账号）在挑选时让位——
 *     先只在有账号的候选里按 2、3 挑；一条有账号的候选都没有时，才按同样顺序在全部候选里挑。也就是说，偏好接口
 *     零账号而别的原生接口有账号 → 走有账号的那条；全部零账号 → 照常选出偏好行（模型照常列出、资料照常，由
 *     N-118 的各自动挑选点把它当不存在；超管页也照常标「上游暂不可用」）。与 N-118「自动挑选处当不存在、选择与
 *     资料照常」同一口径（TC-MODEL-043）。
 *
 * 调用方负责只传同一 base 模型的行（`resolvePiWireRow` 按 base id 汇总后调本函数）。
 */
export function piNativeWireRow(rows) {
    const candidates = rows.filter(isPiNativeCandidate);
    if (candidates.length === 0)
        return undefined;
    const familySlug = rows.find((row) => typeof row.familySlug === "string" && row.familySlug.trim())?.familySlug;
    if (isPiHeldFamily(familySlug))
        return undefined;
    const preference = piWirePreferenceFor(familySlug);
    const pick = (pool) => {
        for (const wire of preference) {
            const hit = pool.find((row) => row.protocol === wire);
            if (hit)
                return hit;
        }
        return undefined;
    };
    return pick(candidates.filter((row) => !isNoCredentialRow(row))) ?? pick(candidates);
}
/** {@link piNativeWireRow} 只要协议的便利形式（同一规则，不是第二份判据）。 */
export function piNativeWireFor(rows) {
    return piNativeWireRow(rows)?.protocol;
}
/** 网关漏发 `translated` 的 pi 告警前缀（固定串，便于在员工机 / 服务端日志里检索）。 */
export const PI_LEG_MISSING_TRANSLATED_LOG = "[model-catalog] pi-leg-missing-translated";
const lastPiMissingTranslatedSignature = new Map();
/**
 * 清单里有 pi 接口行（四种之一、没标不支持）却没带 `translated` 布尔的 model id（去重、保序），非空时打一条
 * 固定前缀的告警。这些行不是 pi 候选（{@link piNativeWireRow}），模型可能因此不进 pi 菜单，或走了另一条
 * 原生接口——日志让它能被发现。解析入口（bundle `toBundle` / 公开清单 `parseByteSpikeManifest`）各调一次；
 * 同一来源缺失集合不变时不重复打（解析在会话热路径上会被反复调用）。
 */
export function warnPiWireRowsMissingTranslated(rows, source) {
    const ids = [...new Set(rows
            .filter((row) => isPiWireProtocol(row.protocol) && row.supported !== false && typeof row.translated !== "boolean")
            .map((row) => row.modelId))];
    const signature = ids.join(",");
    if (ids.length > 0 && lastPiMissingTranslatedSignature.get(source) !== signature) {
        console.warn(`${PI_LEG_MISSING_TRANSLATED_LOG} source=${source} count=${ids.length} models=${signature}`
            + " — gateway omitted `translated`; these rows are not pi candidates (a model with no other native row stays out of pi)");
    }
    lastPiMissingTranslatedSignature.set(source, signature);
    return ids;
}
//# sourceMappingURL=pi-wire.js.map